<template>
  <div class="main-content-container container-fluid px-4">
    <!-- Page Header -->
    <div class="page-header row no-gutters py-4">
      <div class="col-12 col-sm-4 text-center text-sm-left mb-0">
        <span class="text-uppercase page-subtitle">Dashboard</span>
        <h3 class="page-title">My Dashboard</h3>
      </div>
      <div class="col-12 col-sm-8 text-center text-sm-right">
        <d-button theme="primary" @click="showPinDialog = true">
          <i class="material-icons">add</i> Pin Widget
        </d-button>
      </div>
    </div>

    <!-- Pinned Widgets -->
    <div class="row" v-if="dashboardWidgets.length > 0">
      <div
        v-for="dw in sortedDashboardWidgets"
        :key="dw.id"
        :class="getColumnClass(dw.size)"
        class="mb-4"
      >
        <div class="card card-small">
          <div class="card-header border-bottom d-flex justify-content-between align-items-center">
            <h6 class="m-0">
              <i v-if="dw.widget && dw.widget.icon" class="material-icons mr-2">{{ dw.widget.icon }}</i>
              {{ dw.widget ? dw.widget.name : 'Widget' }}
            </h6>
            <div>
              <d-button size="small" outline @click="unpinWidget(dw.id)">
                <i class="material-icons">close</i>
              </d-button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="dw.widget" class="widget-preview">
              <p class="text-muted">{{ dw.widget.description || 'Widget preview' }}</p>
              <div v-if="dw.insight" class="mt-3">
                <small class="text-muted">Insight: {{ dw.insight.name }}</small>
              </div>
            </div>
            <div v-else class="text-muted text-center py-4">
              <i class="material-icons" style="font-size: 48px;">widgets</i>
              <p class="mt-2">Widget content</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div class="row" v-else>
      <div class="col-12">
        <div class="card card-small">
          <div class="card-body text-center py-5">
            <i class="material-icons" style="font-size: 64px; color: #ccc;">dashboard</i>
            <h5 class="mt-3">No widgets pinned</h5>
            <p class="text-muted">Pin widgets to customize your dashboard</p>
            <d-button theme="primary" @click="showPinDialog = true">
              <i class="material-icons">add</i> Pin Widget
            </d-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pin Widget Dialog -->
    <d-modal v-if="showPinDialog" @close="showPinDialog = false" centered>
      <d-modal-header>
        <d-modal-title>Pin Widget to Dashboard</d-modal-title>
      </d-modal-header>
      <d-modal-body>
        <div class="form-group">
          <label>Select Widget</label>
          <select v-model="selectedWidgetId" class="form-control">
            <option value="">Select a widget...</option>
            <option v-for="widget in availableWidgets" :key="widget.id" :value="widget.id">
              {{ widget.name }}
            </option>
          </select>
        </div>
        <div class="form-group" v-if="availableInsights.length > 0">
          <label>Select Insight (Optional)</label>
          <select v-model="selectedInsightId" class="form-control">
            <option value="">None</option>
            <option v-for="insight in availableInsights" :key="insight.id" :value="insight.id">
              {{ insight.name }}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label>Size</label>
          <select v-model="selectedSize" class="form-control">
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </div>
      </d-modal-body>
      <d-modal-footer>
        <d-button theme="secondary" @click="showPinDialog = false">Cancel</d-button>
        <d-button theme="primary" @click="pinWidget" :disabled="!selectedWidgetId">
          Pin Widget
        </d-button>
      </d-modal-footer>
    </d-modal>
  </div>
</template>

<script>
import widgetsAPI from '@/api/widgets';
import insightsAPI from '@/api/insights';

export default {
  name: 'MyDashboard',
  data() {
    return {
      dashboardWidgets: [],
      availableWidgets: [],
      availableInsights: [],
      showPinDialog: false,
      selectedWidgetId: '',
      selectedInsightId: '',
      selectedSize: 'medium',
      loading: false,
    };
  },
  computed: {
    sortedDashboardWidgets() {
      return [...this.dashboardWidgets].sort((a, b) => {
        if (a.column !== b.column) return a.column - b.column;
        return a.position - b.position;
      });
    },
  },
  mounted() {
    this.loadDashboardWidgets();
    this.loadAvailableWidgets();
    this.loadAvailableInsights();
  },
  methods: {
    loadDashboardWidgets() {
      this.loading = true;
      widgetsAPI.getDashboardWidgets()
        .then((response) => {
          this.dashboardWidgets = response.data.dashboard_widgets || [];
        })
        .catch((error) => {
          console.error('Failed to load dashboard widgets:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadAvailableWidgets() {
      widgetsAPI.getWidgets()
        .then((response) => {
          this.availableWidgets = (response.data.widgets || []).filter(w => w.enabled);
        })
        .catch((error) => {
          console.error('Failed to load widgets:', error);
        });
    },
    loadAvailableInsights() {
      insightsAPI.getInsights()
        .then((response) => {
          this.availableInsights = response.data.insights || [];
        })
        .catch((error) => {
          console.error('Failed to load insights:', error);
        });
    },
    pinWidget() {
      if (!this.selectedWidgetId) {
        alert('Please select a widget');
        return;
      }

      const payload = {
        widget_id: parseInt(this.selectedWidgetId),
        size: this.selectedSize,
        position: this.dashboardWidgets.length,
        column: 0,
      };

      if (this.selectedInsightId) {
        payload.insight_id = parseInt(this.selectedInsightId);
      }

      widgetsAPI.pinWidget(payload)
        .then(() => {
          this.showPinDialog = false;
          this.selectedWidgetId = '';
          this.selectedInsightId = '';
          this.selectedSize = 'medium';
          this.loadDashboardWidgets();
        })
        .catch((error) => {
          console.error('Failed to pin widget:', error);
          alert('Failed to pin widget: ' + (error.response && error.response.data && error.response.data.error || error.message));
        });
    },
    unpinWidget(id) {
      if (!confirm('Are you sure you want to unpin this widget?')) {
        return;
      }
      widgetsAPI.unpinWidget(id)
        .then(() => {
          this.loadDashboardWidgets();
        })
        .catch((error) => {
          console.error('Failed to unpin widget:', error);
          alert('Failed to unpin widget: ' + (error.response && error.response.data && error.response.data.error || error.message));
        });
    },
    getColumnClass(size) {
      const classes = {
        small: 'col-md-4',
        medium: 'col-md-6',
        large: 'col-md-12',
      };
      return classes[size] || 'col-md-6';
    },
  },
};
</script>

<style scoped>
.widget-preview {
  min-height: 100px;
}
</style>

